/**
 * @file TwoFactorPrompt.jsx
 * @description Prompts users to set up 2FA after first login
 *
 * @section Responsibilities
 * - Show 2FA setup recommendation
 * - Quick setup flow
 * - Skip option for later
 * - Security education
 *
 * @section Dependencies
 * - TwoFactorSetup component
 * - Session management
 * - Navigation
 *
 * @section Integration Points
 * - Login flow
 * - User onboarding
 * - Security settings
 *
 * @section State Management
 * - showSetup: Controls setup flow
 * - skipCount: Track skip attempts
 * - completed: Setup completion status
 *
 * @section Events
 * - onSetup: Start 2FA setup
 * - onSkip: Skip setup
 * - onComplete: Complete setup
 *
 * @section Props
 * @prop {Function} onComplete - Completion callback
 * @prop {Function} onSkip - Skip callback
 * @prop {Boolean} mandatory - Whether 2FA is mandatory
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import DgButton from "../../components/DgButton";
import TwoFactorSetup from "./TwoFactorSetup";
import "./../../styles/two-factor.css";

const TwoFactorPrompt = ({ open, onComplete, onSkip, mandatory = false }) => {
  const [showSetup, setShowSetup] = useState(false);

  const handleSetupComplete = () => {
    setShowSetup(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
  };

  const handleStartSetup = () => {
    setShowSetup(true);
  };  if (showSetup) {
    return (
      <Dialog 
        open={open} 
        maxWidth="md" 
        fullWidth 
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            backgroundColor: "#021C2D",
            color: "#95B2C5",
            borderRadius: "12px",
            border: "1px solid #274A61"
          }
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(2, 28, 45, 0.8)" // Dark backdrop
          }
        }}
      >
        <DialogContent sx={{ 
          backgroundColor: "#021C2D",
          color: "#95B2C5" 
        }}>
          <TwoFactorSetup
            onComplete={handleSetupComplete}
            showBackupCodes={false} // Changed to false for setup flow to show codes properly
          />
        </DialogContent>
      </Dialog>
    );
  }return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth 
      disableEscapeKeyDown={mandatory}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
          backgroundColor: "#021C2D",
          color: "#95B2C5",
          border: "1px solid #274A61"
        }
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(2, 28, 45, 0.8)" // Dark backdrop instead of white
        }
      }}
      className="two-factor-container"
    ><DialogTitle>
        <Box display="flex" alignItems="center" gap={1} className="two-factor-header">
          <SecurityIcon sx={{ color: "#A1BCA8" }} />
          <Typography variant="h6" sx={{ color: "#eeeeee" }} className="two-factor-title">
            Secure Your Account with Two-Factor Authentication
          </Typography>
        </Box>
      </DialogTitle>        <DialogContent sx={{ 
        backgroundColor: "#021C2D",
        color: "#95B2C5" 
      }}>
        <Alert severity="info" sx={{
          mb: 3,
          backgroundColor: "rgba(161, 188, 168, 0.1)",
          border: "1px solid #A1BCA8",
          color: "#A1BCA8",
          borderRadius: "8px"
        }}>
          We recommend enabling two-factor authentication to add an extra layer of security to your Ad Deum account.
        </Alert>

        <Typography variant="body1" gutterBottom sx={{ mb: 2, color: "#95B2C5" }}>
          Two-factor authentication (2FA) provides additional security by requiring a second form of verification when you sign in.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: "#eeeeee" }}>
            Benefits of enabling 2FA:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <ShieldIcon sx={{ color: "#A1BCA8" }} fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Enhanced account security" 
                primaryTypographyProps={{ color: "#95B2C5" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LockIcon sx={{ color: "#A1BCA8" }} fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Protection against unauthorized access" 
                primaryTypographyProps={{ color: "#95B2C5" }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PhoneIcon sx={{ color: "#A1BCA8" }} fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Works with popular authenticator apps" 
                primaryTypographyProps={{ color: "#95B2C5" }}
              />
            </ListItem>
          </List>
        </Box>

        <Typography variant="body2" sx={{ color: "#95B2C5" }}>
          Setup takes less than 2 minutes and can be done using apps like Google Authenticator, Authy, or Microsoft Authenticator.
        </Typography>

        {mandatory && (
          <Alert severity="warning" sx={{ 
            mt: 2,
            backgroundColor: "rgba(255, 189, 48, 0.1)",
            border: "1px solid #FFBD30",
            color: "#FFBD30",
            borderRadius: "8px"
          }}>
            Two-factor authentication is required for your account type. Please complete the setup to continue.
          </Alert>        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 1,
        backgroundColor: "#021C2D"
      }}>
        {!mandatory && (
          <DgButton
            category="secondary"
            text="Maybe Later"
            action={handleSkip}
            style={{ padding: "16px 32px", fontSize: "16px" }}
          />
        )}
        <DgButton
          category="primary"
          text="Set Up 2FA Now"
          size="large"
          action={handleStartSetup}
          style={{ padding: "16px 32px", fontSize: "16px" }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorPrompt;
